import { useCallback, useEffect, useRef } from 'react';

// ============================================================================
// USE SCAN ALERT SOUND - BARKOD OKUTMA UYARI SESİ (Web Audio API)
// ============================================================================

/** Süpürme sayısı. Süre ile birlikte toplam ~1,8 sn eder. */
const SWEEP_COUNT = 3;
/** Tek süpürmenin süresi (sn). */
const SWEEP_DURATION = 0.45;
/** Süpürmeler arası boşluk (sn). */
const SWEEP_GAP = 0.12;

export interface UseScanAlertSoundReturn {
  /** Uyarı sesini baştan çal. */
  playScanAlertSound: () => void;
  /** Çalan sesi kes. */
  stopScanAlertSound: () => void;
}

/**
 * Okutulan barkod sepete eklenemediğinde çalan uyarı sesi.
 *
 * Kasiyer barkodları ekrana bakmadan okuttuğu için uyarıyı yalnızca kulağıyla
 * fark ediyor; bu yüzden tek bir kısa bip yeterli değil. Üç alçalan süpürme
 * arka arkaya çalınır ve yaklaşık iki saniye sürer.
 *
 * `useAutoAcceptFailureSound`tan farkı: orada alarm kullanıcı onaylayana kadar
 * tekrar ediyor, burada tekrar YOK. Uyarı sepetteki şeritte kalıcı olarak
 * görünmeye devam ettiği için sesin ısrar etmesine gerek yok.
 */
export const useScanAlertSound = (): UseScanAlertSoundReturn => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const stopScanAlertSound = useCallback(() => {
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // Zaten kapalı olabilir
      }
      audioContextRef.current = null;
    }
  }, []);

  const playScanAlertSound = useCallback(() => {
    // Arka arkaya okutmada sesler üst üste binmesin
    stopScanAlertSound();

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      const now = audioContext.currentTime;

      for (let i = 0; i < SWEEP_COUNT; i += 1) {
        const start = now + i * (SWEEP_DURATION + SWEEP_GAP);
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Sert `sawtooth` + aşağı kayan frekans: "bir şeyler ters gitti" hissi.
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(880, start);
        oscillator.frequency.exponentialRampToValueAtTime(260, start + SWEEP_DURATION);

        gainNode.gain.setValueAtTime(0, start);
        gainNode.gain.linearRampToValueAtTime(0.32, start + 0.03);
        gainNode.gain.setValueAtTime(0.32, start + SWEEP_DURATION - 0.06);
        gainNode.gain.linearRampToValueAtTime(0, start + SWEEP_DURATION);

        oscillator.start(start);
        oscillator.stop(start + SWEEP_DURATION);
      }
    } catch (error) {
      console.error('🔔 [ScanAlertSound] Ses çalınamadı:', error);
    }
  }, [stopScanAlertSound]);

  useEffect(() => stopScanAlertSound, [stopScanAlertSound]);

  return { playScanAlertSound, stopScanAlertSound };
};

export default useScanAlertSound;
